import AHBoolean from "_shared/booleans/boolean";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import type Session from "users/__session/session";
import ProposalTemplate, { ProposalTemplateParty } from "work/proposal-templates/proposal-template";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import {
  withClientTeam,
  withVendorTeamAndFeeSchedule,
} from "work/proposals/drafts-from-previous/draft-from-previous-proposal";
import ProposalTeamReuse from "work/proposals/drafts-from-previous/proposal-team-reuse";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import ClientTeam from "work/work-agreements/teams/client-team/client-team";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

/** What a new draft started from a proposal template holds, and what it could not take. */
export type DraftFromProposalTemplate = {
  builder: ProposalBuilder;
  /** People on the template's team who have left the organization since it was saved. */
  peopleWhoLeft: EntityMember[];
  /** The template's team leader, when they are still here and can no longer sign. */
  leadersWhoCannotSign: EntityMember[];
  /** How many of the template's documents were deleted since it was saved. */
  unavailableDocumentCount: number;
};

/**
 * Builds a new, unsaved draft from a saved proposal template (#6129).
 *
 * The template is one side of a proposal, so the draft gets that side and the defaults a new draft
 * has for the other: no counterparty, no dates. Its people go through the same rules as a draft
 * started from a previous proposal (#1569), with the same code: anybody who has left is dropped, and
 * so is a leader who can no longer sign. Its documents are marked to be copied on the first save, so
 * the template's own copies are never linked to the proposal.
 *
 * When the saver kept the other party's team (#6637), the draft seats it on that party's side, under
 * the same rules, with the team templates applied to it.
 *
 * Throws when the template was saved on the other side from the one the session is on: a firm's
 * template would put its people on the client's team.
 */
export function buildDraftFromProposalTemplate(args: {
  template: ProposalTemplate;
  session: Session;
}): DraftFromProposalTemplate {
  const { template, session } = args;
  if (!template.isUsableIn(session)) {
    throw new Error("A proposal template can start a draft only on the side it was saved from.");
  }

  const teamReuse = template.teamReuse ?? ProposalTeamReuse.NOBODY_TO_LEAVE_OUT;
  const peopleWhoLeft: EntityMember[] = [];
  const leadersWhoCannotSign: EntityMember[] = [];
  const noteDropped = (person: EntityMember) => {
    const list = teamReuse.hasLeft(person) ? peopleWhoLeft : leadersWhoCannotSign;
    if (!list.some((other) => other.isEqualTo(person))) list.push(person);
  };

  let builder = new ProposalBuilder({ session });
  if (template.proposalName) builder = builder.setName(new ProjectName(template.proposalName));
  if (template.description) builder = builder.setDescription(new ProjectDescription(template.description));
  builder = builder.setNegotiable(template.negotiable);
  builder = builder.setTeamRestricted(new AHBoolean(template.teamRestricted));

  const toCopy = (documents: WorkDocument[]) => documents.map((document) => document.toBeCopiedOnSave());

  if (template.party === ProposalTemplateParty.Vendor) {
    if (template.counterpartyTeam) {
      builder = withClientTeam(
        builder,
        new ClientTeam({
          leader: template.counterpartyTeam.leader,
          members: template.counterpartyTeam.members,
        }),
        teamReuse,
        noteDropped,
      );
    }
    builder = builder.setClientTeamTemplateIds(cloneIds(template.counterpartyTeamTemplateIds));
    builder = withVendorTeamAndFeeSchedule({
      builder,
      team: template.team
        ? new VendorTeam({
            leader: template.team.leader,
            members: template.team.members,
            marketplaceId: template.team.marketplaceId,
          })
        : undefined,
      feeSchedule: template.feeSchedule,
      teamReuse,
      noteDropped,
    });
    builder = builder
      .setVendorTeamTemplateIds(cloneIds(template.teamTemplateIds))
      .setVendorFeeScheduleTemplateIds(cloneIds(template.feeScheduleTemplateIds));
    if (template.policies.length > 0) builder = builder.setVendorPolicies(toCopy(template.policies));
  } else {
    // The firm's team goes in before the client's rates, which name nobody, so seating it leaves them be.
    builder = withVendorTeamAndFeeSchedule({
      builder,
      team: template.counterpartyTeam
        ? new VendorTeam({
            leader: template.counterpartyTeam.leader,
            members: template.counterpartyTeam.members,
            marketplaceId: template.counterpartyTeam.marketplaceId,
          })
        : undefined,
      feeSchedule: undefined,
      teamReuse,
      noteDropped,
    });
    builder = builder.setVendorTeamTemplateIds(cloneIds(template.counterpartyTeamTemplateIds));
    if (template.team) {
      builder = withClientTeam(
        builder,
        new ClientTeam({
          leader: template.team.leader,
          members: template.team.members,
        }),
        teamReuse,
        noteDropped,
      );
    }
    if (template.feeSchedule.length > 0) builder = builder.setFeeSchedule(template.feeSchedule);
    builder = builder
      .setClientTeamTemplateIds(cloneIds(template.teamTemplateIds))
      .setClientFeeScheduleTemplateIds(cloneIds(template.feeScheduleTemplateIds));
    if (template.policies.length > 0) builder = builder.setClientPolicies(toCopy(template.policies));
  }

  // Always, even at 0%: a new draft starts with the organization's default discount, and a template
  // saved at none means none.
  builder = builder.setDiscount(template.discount);
  builder = builder.setConflictsCheckWaived(new AHBoolean(template.conflictsCheckWaived));
  if (template.conflicts.length > 0) builder = builder.setConflicts(toCopy(template.conflicts));

  return {
    builder,
    peopleWhoLeft,
    leadersWhoCannotSign,
    unavailableDocumentCount: template.unavailableDocumentCount,
  };
}

function cloneIds(ids: Guid[]): Guid[] {
  return ids.map((id) => id.clone());
}
